import FocusManager from '../navigation/focus-manager';

/**
 * Desktop Mega Menu Navigation (3-level)
 * - Column 1: Level 1 items
 * - Column 2: Level 2 items (shown when level 1 is clicked)
 * - Column 3: Level 3 items (shown when level 2 is clicked)
 */

interface MegaMenuState {
  openMenuId: number | null;
  activeLevel1Id: number | null;
  activeLevel2Id: number | null;
}

class NavBar {
  private state: MegaMenuState = {
    openMenuId: null,
    activeLevel1Id: null,
    activeLevel2Id: null,
  };

  // Minimum gap (px) to keep between the mega menu and the viewport edge
  private readonly MEGA_MENU_EDGE_GUTTER = 24;

  private triggers: NodeListOf<HTMLElement> = document.querySelectorAll(
    "[data-mega-trigger]",
  );
  private menus: NodeListOf<HTMLElement> =
    document.querySelectorAll("[data-mega-menu]");

  // Search Elements
  private searchBtn: HTMLElement | null = document.querySelector(".search-btn");
  private searchOverlay: HTMLElement | null = document.querySelector(
    "[data-search-overlay]",
  );
  private focusManager = new FocusManager();
  private focusTrapCleanup: (() => void) | null = null;
  private lastFocusedElement: HTMLElement | null = null;
  private searchCloseBtn: HTMLElement | null = document.querySelector(
    "[data-search-close]",
  );
  private searchInput: HTMLInputElement | null = document.querySelector(
    "[data-search-input]",
  );

  static init() {
    const instance = new NavBar();
    instance.bindEvents();
  }

  private bindEvents(): void {
    if (this.searchBtn) {
      this.searchBtn.setAttribute("aria-expanded", "false");
    }

    // --- SEARCH EVENTS ---
    if (this.searchBtn && this.searchOverlay) {
      this.searchBtn.addEventListener("click", (e) => {
        e.preventDefault();
        this.openSearch();
      });
    }

    if (this.searchCloseBtn) {
      this.searchCloseBtn.addEventListener("click", (e) => {
        e.preventDefault();
        this.closeSearch();
      });
    }

    // Trigger buttons to open/close mega menu
    this.triggers.forEach((trigger) => {
      trigger.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        const menuId = trigger.getAttribute("data-mega-trigger");
        this.toggleMegaMenu(menuId as unknown as number);
      });
    });

    // Bind level 1 links (column 1)
    this.menus.forEach((menu) => {
      const col1 = menu.querySelector(".mega-menu__col--level1");
      if (col1) {
        const level1Links = col1.querySelectorAll(
          ".mega-menu__link--level1",
        ) as NodeListOf<HTMLAnchorElement>;

        level1Links.forEach((link) => {
          link.addEventListener("click", (e) => {
            const level1Id = link.getAttribute("data-level1-id");
            if (level1Id) {
              e.preventDefault();
              e.stopPropagation();
              this.selectLevel1(parseInt(level1Id), menu);
            }
          });
        });
      }
    });

    // Bind level 2 links (column 2)
    this.menus.forEach((menu) => {
      const col2 = menu.querySelector(".mega-menu__col--level2");
      if (col2) {
        const level2Links = col2.querySelectorAll(
          ".mega-menu__link--level2",
        ) as NodeListOf<HTMLAnchorElement>;

        level2Links.forEach((link) => {
          link.addEventListener("click", (e) => {
            const level2Id = link.getAttribute("data-level2-id");
            if (level2Id) {
              e.preventDefault();
              e.stopPropagation();
              this.selectLevel2(parseInt(level2Id), menu);
            }
          });
        });
      }
    });

    // Close menu when clicking outside
    document.addEventListener("click", (e) => {
      const target = e.target as HTMLElement;
      const isInMenu =
        target.closest("[data-mega-menu]") ||
        target.closest("[data-mega-trigger]");

      if (!isInMenu && this.state.openMenuId !== null) {
        this.closeAllMenus();
      }
    });

    // Close on Escape key
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        // Close menu
        if (this.state.openMenuId !== null) {
          this.closeAllMenus();
        }
        // Close search overlay
        if (this.searchOverlay?.classList.contains("is-open")) {
          this.closeSearch();
        }
      }
    });

    // Re-clamp the open menu's position if the viewport changes size
    window.addEventListener("resize", () => {
      if (this.state.openMenuId === null) return;

      const openMenu = document.querySelector(
        `[data-mega-menu="${this.state.openMenuId}"]`,
      ) as HTMLElement | null;

      if (openMenu) {
        this.adjustMegaMenuPosition(openMenu);
      }
    });
  }

  // --- SEARCH METHODS ---
  private openSearch(): void {
    this.closeAllMenus(); // Close mega menu if open
    this.lastFocusedElement = document.activeElement as HTMLElement | null;

    if (this.searchOverlay) {
      this.searchOverlay.hidden = false;
      this.searchOverlay.classList.add("is-open");
      document.body.style.overflow = "hidden"; // Prevent background scrolling

      if (this.searchBtn) {
        this.searchBtn.setAttribute("aria-expanded", "true");
      }

      this.focusManager.saveFocus();
      this.focusManager.focusFirstElement(this.searchOverlay);
      this.focusTrapCleanup = this.focusManager.trapFocus(this.searchOverlay);

      // Add slight delay to focus so the transition can finish
      setTimeout(() => {
        this.searchInput?.focus();
      }, 300);
    }
  }

  private closeSearch(): void {
    if (this.searchOverlay) {
      this.searchOverlay.classList.remove("is-open");
      this.searchOverlay.hidden = true;
      document.body.style.overflow = ""; // Restore scrolling
    }

    if (this.searchBtn) {
      this.searchBtn.setAttribute("aria-expanded", "false");
    }

    if (this.focusTrapCleanup) {
      this.focusTrapCleanup();
      this.focusTrapCleanup = null;
    }

    this.focusManager.restoreFocus();
    this.focusManager.releaseFocus();
  }

  // --- POSITIONING ---
  private adjustMegaMenuPosition(menu: HTMLElement): void {
    const gutter = this.MEGA_MENU_EDGE_GUTTER;

    const containingBlock = menu.offsetParent as HTMLElement | null;
    if (!containingBlock) return;

    const anchorRect = containingBlock.getBoundingClientRect();

    // Use getBoundingClientRect().width for sub-pixel precision
    const menuWidth = menu.getBoundingClientRect().width;

    const anchorCenter = anchorRect.left + anchorRect.width / 2;
    const naturalLeft = anchorCenter - menuWidth / 2;
    const naturalRight = anchorCenter + menuWidth / 2;

    let shift = 0;

    // CRITICAL: Use documentElement.clientWidth instead of window.innerWidth
    // This perfectly excludes the vertical scrollbar width from the math.
    const viewportWidth = document.documentElement.clientWidth;

    const overflowRight = naturalRight - (viewportWidth - gutter);
    const overflowLeft = gutter - naturalLeft;

    if (overflowRight > 0) {
      shift = -overflowRight;
    } else if (overflowLeft > 0) {
      shift = overflowLeft;
    }

    // Failsafe: perfectly center if it's somehow wider than the viewport
    const availableWidth = viewportWidth - gutter * 2;
    if (menuWidth > availableWidth) {
      shift = viewportWidth / 2 - anchorCenter;
    }

    menu.style.setProperty("--mega-shift", `${shift}px`);
  }

  // --- MENU METHODS ---
  private toggleMegaMenu(menuId: number): void {
    if (this.state.openMenuId === menuId) {
      this.closeAllMenus();
    } else {
      this.closeAllMenus();
      this.state.openMenuId = menuId;

      const menu = document.querySelector(
        `[data-mega-menu="${menuId}"]`,
      ) as HTMLElement;
      const trigger = document.querySelector(
        `[data-mega-trigger="${menuId}"]`,
      ) as HTMLElement;

      if (menu && trigger) {
        const col1 = menu.querySelector(
          ".mega-menu__col--level1",
        ) as HTMLElement;
        const col2 = menu.querySelector(
          ".mega-menu__col--level2",
        ) as HTMLElement;
        const col3 = menu.querySelector(
          ".mega-menu__col--level3",
        ) as HTMLElement;

        if (col1) col1.classList.add("is-visible");
        if (col2) col2.classList.remove("is-visible");
        if (col3) col3.classList.remove("is-visible");

        // Apply classes so the DOM gives it physical width
        menu.classList.add("is-open");
        menu.hidden = false;
        trigger.classList.add("is-active");
        trigger.setAttribute("aria-expanded", "true");

        // Calculate shift instantly before the next paint
        this.adjustMegaMenuPosition(menu);
      }
    }
  }

  private selectLevel1(level1Id: number, menu: HTMLElement): void {
    this.state.activeLevel1Id = level1Id;
    this.state.activeLevel2Id = null;

    const col1 = menu.querySelector(".mega-menu__col--level1");
    if (col1) {
      const allLinks = col1.querySelectorAll(".mega-menu__link--level1");
      allLinks.forEach((link) => link.classList.remove("is-active"));
      const activeLink = col1.querySelector(
        `[data-level1-id="${level1Id}"]`,
      ) as HTMLElement;
      if (activeLink) activeLink.classList.add("is-active");
    }

    const col2 = menu.querySelector(".mega-menu__col--level2") as HTMLElement;
    if (col2) {
      const allGroups = col2.querySelectorAll(".mega-menu__group");
      allGroups.forEach((group) => group.classList.remove("is-visible"));

      const activeGroup = col2.querySelector(
        `[data-level1-group="${level1Id}"]`,
      ) as HTMLElement;
      if (activeGroup) activeGroup.classList.add("is-visible");

      col2.classList.add("is-visible");
    }

    const col3 = menu.querySelector(".mega-menu__col--level3") as HTMLElement;
    if (col3) col3.classList.remove("is-visible");

    // Wait for the browser to render the new column's layout width, then instantly shift
    requestAnimationFrame(() => {
      this.adjustMegaMenuPosition(menu);
    });
  }

  private selectLevel2(level2Id: number, menu: HTMLElement): void {
    this.state.activeLevel2Id = level2Id;

    const col2 = menu.querySelector(".mega-menu__col--level2");
    if (col2) {
      const allLinks = col2.querySelectorAll(".mega-menu__link--level2");
      allLinks.forEach((link) => link.classList.remove("is-active"));

      const activeLink = col2.querySelector(
        `[data-level2-id="${level2Id}"]`,
      ) as HTMLElement;
      if (activeLink) activeLink.classList.add("is-active");
    }

    const col3 = menu.querySelector(".mega-menu__col--level3") as HTMLElement;
    if (col3) {
      const allGroups = col3.querySelectorAll(".mega-menu__group--level3");
      allGroups.forEach((group) => group.classList.remove("is-visible"));

      const activeGroup = col3.querySelector(
        `[data-level2-group="${level2Id}"]`,
      ) as HTMLElement;
      if (activeGroup) activeGroup.classList.add("is-visible");

      col3.classList.add("is-visible");
    }

    // Wait for the browser to render the new column's layout width, then instantly shift
    requestAnimationFrame(() => {
      this.adjustMegaMenuPosition(menu);
    });
  }

  /* (selectLevel1 and selectLevel2 stay exactly the same) */

  private closeAllMenus(): void {
    this.state.openMenuId = null;
    this.state.activeLevel1Id = null;
    this.state.activeLevel2Id = null;

    this.triggers.forEach((trigger) => {
      trigger.classList.remove("is-active");
      trigger.setAttribute("aria-expanded", "false");
    });

    this.menus.forEach((menu) => {
      menu.classList.remove("is-open");
      menu.hidden = true;

      // Removed: menu.style.setProperty('--mega-shift', '0px');
      // ^ Letting the shift persist prevents the menu from sliding back to the middle during the fade out transition

      const col1 = menu.querySelector(".mega-menu__col--level1") as HTMLElement;
      const col2 = menu.querySelector(".mega-menu__col--level2") as HTMLElement;
      const col3 = menu.querySelector(".mega-menu__col--level3") as HTMLElement;

      if (col1) {
        col1.classList.remove("is-visible");
        col1.querySelectorAll(".mega-menu__link--level1").forEach((link) => {
          link.classList.remove("is-active");
        });
      }

      if (col2) {
        col2.classList.remove("is-visible");
        col2.querySelectorAll(".mega-menu__group").forEach((group) => {
          group.classList.remove("is-visible");
        });
        col2.querySelectorAll(".mega-menu__link--level2").forEach((link) => {
          link.classList.remove("is-active");
        });
      }

      if (col3) {
        col3.classList.remove("is-visible");
        col3.querySelectorAll(".mega-menu__group--level3").forEach((group) => {
          group.classList.remove("is-visible");
        });
      }
    });
  }
}

export default NavBar;
